import React, { useCallback, useEffect, useState } from 'react'

import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  Skeleton,
  SxProps,
} from '@mui/material'

import styles from './styles.module.scss'

export interface ItemProps {
  label: string
  value: string | React.ReactNode
  description?: string
}

interface AccordionListItemProps {
  header: {
    cellSizes: number[]
    items: ItemProps[]
  }
  renderContent?: React.ReactNode
  defaultExpanded?: boolean | false
  loading?: boolean | false
  closeAccordion?: boolean
  customOnClick?: () => void
  onFirstOpen?: () => void
  sx?: SxProps
}

export const AccordionListItem = ({
  header,
  renderContent,
  defaultExpanded,
  loading,
  closeAccordion,
  customOnClick,
  onFirstOpen,
  sx,
}: AccordionListItemProps) => {
  const [expanded, setExpanded] = useState(false)
  const [firstOpen, setFirstOpen] = useState(true)

  useEffect(() => {
    closeAccordion && setExpanded(false)
  }, [closeAccordion])

  const handleFirstOpen = useCallback(() => {
    setFirstOpen(false)
    if (onFirstOpen) {
      onFirstOpen()
    }
  }, [onFirstOpen])

  useEffect(() => {
    if (defaultExpanded) {
      handleFirstOpen()
    }
  }, [defaultExpanded, handleFirstOpen])

  const handleClick = () => {
    if (customOnClick) {
      customOnClick()
    } else {
      setExpanded(!expanded)

      if (firstOpen) {
        handleFirstOpen()
      }
    }
  }

  return (
    <Accordion
      onClick={handleClick}
      className={styles.accordionContainer}
      defaultExpanded={defaultExpanded}
      disabled={loading}
      expanded={closeAccordion}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="panel1bh-content"
        id="panel1bh-header"
        sx={{ ...sx, width: '100%' }}
      >
        <Grid container spacing={2}>
          {header.items.map(({ label, value }, index) => {
            return (
              <Grid item key={index} xs={12} sm={header.cellSizes[index]}>
                <div className={styles.title}>{label}</div>
                <div className={styles.value}>
                  {loading ? <Skeleton animation="wave" /> : value}
                </div>
              </Grid>
            )
          })}
        </Grid>
      </AccordionSummary>
      <AccordionDetails>{renderContent}</AccordionDetails>
    </Accordion>
  )
}
